import axios, { AxiosError } from "axios";
import { env } from "@/config/environment";

export const api = axios.create({
  baseURL: env.API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: unwrap backend envelope + handle 401
api.interceptors.response.use(
  (response) => {
    if (
      response.data &&
      typeof response.data === "object" &&
      "data" in response.data
    ) {
      if ("meta" in response.data) {
        // Paginated response — preserve both data and meta
        response.data = { data: response.data.data, meta: response.data.meta };
      } else if ("warnings" in response.data) {
        // A write that succeeded with caveats (feed production over a short
        // stock): keep the warnings beside the data, or the screen never
        // hears about them. The backend omits an empty `warnings`, so such an
        // endpoint resolves to TWO shapes — the bare row or {data, warnings}.
        // Its service must normalise (see feedProductionService).
        response.data = {
          data: response.data.data,
          warnings: response.data.warnings,
        };
      } else {
        response.data = response.data.data;
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401 && error.config) {
      const isAuthEndpoint = error.config.url?.includes("/auth/");
      if (!isAuthEndpoint) {
        localStorage.removeItem("accessToken");
        window.location.href = "/signin";
      }
    }
    return Promise.reject(error);
  }
);

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: string; message?: string }>;

    if (axiosError.response?.data) {
      const data = axiosError.response.data;
      if (typeof data.error === "string") return data.error;
      if (typeof data.message === "string") return data.message;
    }

    if (axiosError.code === "ERR_NETWORK") {
      return "Cannot connect to server. Please check your connection.";
    }

    return axiosError.message;
  }
  return "An unexpected error occurred";
};
